/*******************************************************************************
 * Copyright     : Copyright (c)  2024 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc.No part may be reproduced or transmitted in any form by any
 *                 means or for anypurpose without the express written permission of Honeywell. *
 *******************************************************************************/
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getBearerToken, getIAMProjectID } from "../../utils/utility";
import { ConfigurationVariables } from "../../config";
import { RBM_ACCESS_TOKEN_STORAGE_NAME } from "../../utils/constants";

/**
 * API service to handle downloading the Gateway Report Configuration File.
 *
 * This service uses Redux Toolkit's `createApi` to define an API slice for
 * downloading the configuration file from a specified Azure Blob Storage URL.

 *
 * @function
 * @name downloadGatewayReportConfigFile
 * @description Defines the API slice for downloading the Gateway Report Configuration File.
 *
 * @param {Object} builder - The builder object used to define the query endpoint.
 * @param {Function} builder.query - Function to define the query endpoint.
 *
 * @returns {Object} The API slice with the defined query endpoint.
 *
 * @example
 * // Usage in a React component
 * import { useGetDownFileSasUriQuery } from 'path/to/services/file-service-api/fileServce';
 *
 * const MyComponent = () => {
 *   const { data, error, isLoading } = useGetDownFileSasUriQuery();
 *
 *   if (isLoading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return <div>Data: {data}</div>;
 * };
 */

/**@TODO: Replace the URL with the actual Azure Blob Storage URL */
export const fileServeApi = createApi({
  reducerPath: "fileServeServiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: ConfigurationVariables.API_BASE_HOST_URL,
    prepareHeaders: async (headers) => {
      try {
        headers.set("authorization", await getBearerToken(RBM_ACCESS_TOKEN_STORAGE_NAME));
        headers.set("SI-RootNodeId", getIAMProjectID());
        headers.set("ProjectId", getIAMProjectID());
      } catch (error) {}

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getDownFileSasUri: builder.query({
      query: ({ systemGuid, category, fileName }) =>
        `/api/systems/${systemGuid}/downloadfile?category=${category}&blobname=${fileName}`,
      keepUnusedDataFor: 0
    }),
    getGatewayVersionDetailsApi: builder.query({
      query: ({ systemGuid }) =>
        `/api/systems/${systemGuid}/details?category=gateway-device`
    }),
    getMetaDataApi: builder.query({
      query: ({ systemGuid, category }) =>
        `/api/systems/${systemGuid}/metadata?category=${category}`
    }),
    sendCommand: builder.mutation({
      query: ({ systemGuid, category }) => ({
        url: `/api/systems/${systemGuid}/v2/sendCommand?category=${category}`,
        method: "POST",
        body: JSON.stringify({ systemGuid, category })
      })
    }),
    syncOrganization: builder.mutation({
      query: ({ orgId, resourcesIds }) => ({
        url: `/api/sync/organization`,
        method: "POST",
        body: JSON.stringify({
          OrganizationId: orgId,
          ResourceIds: resourcesIds 
        })
      })
    })
  })
});

export const {
  useGetDownFileSasUriQuery,
  useGetGatewayVersionDetailsApiQuery,
  useGetMetaDataApiQuery,
  useSendCommandMutation,
  useSyncOrganizationMutation
} = fileServeApi;